'use client';

import { useTranslations, useLocale } from 'next-intl';
import { formatDate, timeAgo, type DateTranslations, type TimeAgoTranslations } from '@ui/lib/date-utils';

/**
 * Custom hook that provides localized date formatting functions
 */
export function useDateFormatting() {
  const tCommon = useTranslations('Common');
  const locale = useLocale();

  const createDateTranslations = (_variant: 'overview' | 'default'): DateTranslations => ({
    today: tCommon('time.today'),
    oneDayAgo: tCommon('time.oneDayAgo'),
    daysAgo: (days: number) => tCommon('time.daysAgo', { days }),
    weeksAgo: (weeks: number) => tCommon('time.weeksAgo', { weeks }),
    never: tCommon('time.never'),
  });

  const createTimeAgoTranslations = (): TimeAgoTranslations => ({
    secondsAgo: (seconds: number) => tCommon('time.timeAgo.secondsAgo', { seconds }),
    minutesAgo: (minutes: number) => tCommon('time.timeAgo.minutesAgo', { minutes }),
    hoursAgo: (hours: number) => tCommon('time.timeAgo.hoursAgo', { hours }),
    daysAgo: (days: number) => tCommon('time.timeAgo.daysAgo', { days }),
    weeksAgo: (weeks: number) => tCommon('time.timeAgo.weeksAgo', { weeks }),
    monthsAgo: (months: number) => tCommon('time.timeAgo.monthsAgo', { months }),
    yearsAgo: (years: number) => tCommon('time.timeAgo.yearsAgo', { years }),
  });

  return {
    /**
     * Format a date with localization support
     * @param date - The date to format
     * @param variant - The formatting variant
     */
    formatDate: (date: Date | string | null, variant: 'overview' | 'default' = 'default') => {
      return formatDate(date, variant, locale, createDateTranslations(variant));
    },

    /**
     * Format a date as time ago with localization support
     * @param date - The date to format
     */
    timeAgo: (date: string | Date) => {
      return timeAgo(date, createTimeAgoTranslations());
    },

    /**
     * Get the current locale
     */
    locale,
  };
}
